/**
 * Schema.org JSON-LD builders for SEO surfaces.
 *
 * Lifted from narcis/packs/seo.ts and inline JSON-LD in narcis/_layout.* on 2026-04-30
 * (post-redesign code-quality pass). Quies hand-rolls the same three core shapes
 * (BreadcrumbList, FAQPage, WebApplication) in five places — those will migrate to
 * these builders in a future commit.
 *
 * Two patterns:
 *  - Structured builders (buildBreadcrumbList, buildFAQPage, buildHowTo, buildProduct,
 *    buildDefinedTerm, buildDefinedTermSet, buildWebApplication, buildSoftwareApplication):
 *    take a typed options object, return a JSON-LD ready to JSON.stringify.
 *  - jsonLd(type, fields): minimal generic helper for one-off types (WebPage, ImageGallery)
 *    where building a typed wrapper would be more code than it saves.
 *
 * Output convention: every builder emits @context first, then @type, then the rest of
 * the fields in the order the input object provided them. Field order matters for byte-
 * level snapshot equivalence with prior hand-rolled schemas.
 */

import {mirroraiOrganization} from '@core/products/organization'

const SCHEMA_ORG = 'https://schema.org'

export type JsonLd = Record<string, unknown>

/** Minimal generic helper. Adds @context + @type, spreads fields. Useful for one-off types. */
export function jsonLd(type: string, fields: Record<string, unknown> = {}): JsonLd {
    return {
        '@context': SCHEMA_ORG,
        '@type': type,
        ...fields,
    }
}

// ── BreadcrumbList ──────────────────────────────────────────────────────────────

export interface BreadcrumbItem {
    name: string
    url: string
}

export function buildBreadcrumbList(items: BreadcrumbItem[]): JsonLd {
    return jsonLd('BreadcrumbList', {
        itemListElement: items.map((item, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: item.name,
            item: item.url,
        })),
    })
}

// ── FAQPage ─────────────────────────────────────────────────────────────────────

export interface FAQItem {
    q: string
    a: string
}

export function buildFAQPage(items: FAQItem[]): JsonLd {
    return jsonLd('FAQPage', {
        mainEntity: items.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: {'@type': 'Answer', text: item.a},
        })),
    })
}

// ── HowTo ───────────────────────────────────────────────────────────────────────

export interface HowToStep {
    name: string
    text: string
}

export interface HowToOptions {
    name: string
    description: string
    /** ISO 8601 duration, e.g. "PT20S" for 20 seconds. */
    totalTime?: string
    /** Optional tool name — wraps in {'@type': 'HowToTool', name}. */
    toolName?: string
    steps: HowToStep[]
    /** Optional creator object — typically `mirroraiOrganization`. */
    creator?: object
}

export function buildHowTo(opts: HowToOptions): JsonLd {
    return jsonLd('HowTo', {
        name: opts.name,
        description: opts.description,
        ...(opts.totalTime && {totalTime: opts.totalTime}),
        ...(opts.toolName && {tool: {'@type': 'HowToTool', name: opts.toolName}}),
        step: opts.steps.map((s) => ({
            '@type': 'HowToStep',
            name: s.name,
            text: s.text,
        })),
        ...(opts.creator && {creator: opts.creator}),
    })
}

// ── Product ─────────────────────────────────────────────────────────────────────

export interface ProductOffer {
    price: string | number
    priceCurrency: string
    availability?: string
    url?: string
    seller?: object
    /** OfferShippingDetails block — required for Merchant listings. See `digitalFreeShipping`. */
    shippingDetails?: object | object[]
    /** MerchantReturnPolicy block — required for Merchant listings. See `digitalReturnPolicy14d`. */
    hasMerchantReturnPolicy?: object
}

export interface ProductOptions {
    name: string
    description: string
    url?: string
    image?: string | string[]
    /**
     * Brand. Pass an object for full Organization, or a string to wrap in
     * {'@type': 'Brand', name}.
     */
    brand: object | string
    offer: ProductOffer
}

export function buildProduct(opts: ProductOptions): JsonLd {
    const brand = typeof opts.brand === 'string' ? {'@type': 'Brand', name: opts.brand} : opts.brand
    return jsonLd('Product', {
        name: opts.name,
        description: opts.description,
        ...(opts.url && {url: opts.url}),
        ...(opts.image && {image: opts.image}),
        brand,
        offers: {
            '@type': 'Offer',
            ...opts.offer,
        },
    })
}

// ── OfferShippingDetails / MerchantReturnPolicy ──────────────────────────────────
//
// Helpers for digital goods. Narcis.ai sells tokens and pack outputs — both deliver
// instantly to the buyer's account, no physical shipping. Google's Merchant listings
// spec still requires `shippingDetails` and `hasMerchantReturnPolicy` on Offer to
// avoid critical/non-critical structured-data warnings; we emit a free, instant,
// multi-region shipping block and a 14-day return window per the pricing FAQ.
//
// Country list mirrors the markets we accept payment from in EUR/USD/GBP. `WW`
// is not a valid ISO 3166-1 alpha-2 code; per Google guidance, list specific
// markets and add more as needed.

export const DIGITAL_GOODS_COUNTRIES = ['DE', 'FR', 'GB', 'US', 'NL', 'IE', 'ES', 'IT', 'BE', 'CA', 'AU']

/**
 * Free, instant, worldwide-list digital "shipping". 0 EUR rate, 0-day handling and
 * transit. Pass a different currency for non-EUR offers.
 */
export function digitalFreeShipping(currency: string = 'EUR'): object {
    return {
        '@type': 'OfferShippingDetails',
        shippingRate: {
            '@type': 'MonetaryAmount',
            value: '0',
            currency,
        },
        shippingDestination: DIGITAL_GOODS_COUNTRIES.map((c) => ({
            '@type': 'DefinedRegion',
            addressCountry: c,
        })),
        deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: {'@type': 'QuantitativeValue', minValue: 0, maxValue: 0, unitCode: 'DAY'},
            transitTime: {'@type': 'QuantitativeValue', minValue: 0, maxValue: 0, unitCode: 'DAY'},
        },
    }
}

/**
 * 14-day finite return window, free returns by mail. Aligns with the Narcis.ai
 * pricing FAQ: unused tokens refundable within 14 days. Pack-specific exclusions
 * (already-rendered preview is non-refundable) are documented in ToS, not the
 * marketing schema.
 */
export function digitalReturnPolicy14d(): object {
    return {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: DIGITAL_GOODS_COUNTRIES,
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
    }
}

// ── DefinedTerm / DefinedTermSet ────────────────────────────────────────────────

export interface DefinedTermOptions {
    name: string
    description: string
    /** Optional set name; wraps in {'@type': 'DefinedTermSet', name}. */
    setName?: string
}

export function buildDefinedTerm(opts: DefinedTermOptions): JsonLd {
    return jsonLd('DefinedTerm', {
        name: opts.name,
        description: opts.description,
        ...(opts.setName && {
            inDefinedTermSet: {'@type': 'DefinedTermSet', name: opts.setName},
        }),
    })
}

export interface DefinedTermSetOptions {
    name: string
    terms: Array<{name: string; description: string}>
}

export function buildDefinedTermSet(opts: DefinedTermSetOptions): JsonLd {
    return jsonLd('DefinedTermSet', {
        name: opts.name,
        hasDefinedTerm: opts.terms.map((t) => ({
            '@type': 'DefinedTerm',
            name: t.name,
            description: t.description,
        })),
    })
}

// ── WebApplication / SoftwareApplication ────────────────────────────────────────

export interface ApplicationOffer {
    /** Often omitted on WebApplication when the price lives on a Product schema instead. */
    price?: string | number
    priceCurrency: string
    description?: string
}

export interface WebApplicationOptions {
    name: string
    alternateName?: string[]
    description: string
    url: string
    applicationCategory: string
    /** Default 'Web'. */
    operatingSystem?: string
    offers?: ApplicationOffer
    featureList?: string[]
    keywords?: string
    screenshot?: string
    /** Default `mirroraiOrganization`. Pass `null` to omit entirely. */
    creator?: object | null
}

function buildApplication(type: 'WebApplication' | 'SoftwareApplication', opts: WebApplicationOptions): JsonLd {
    const creator = opts.creator === undefined ? mirroraiOrganization : opts.creator
    return jsonLd(type, {
        name: opts.name,
        ...(opts.alternateName && {alternateName: opts.alternateName}),
        description: opts.description,
        url: opts.url,
        applicationCategory: opts.applicationCategory,
        operatingSystem: opts.operatingSystem ?? 'Web',
        ...(opts.offers && {
            offers: {
                '@type': 'Offer',
                ...opts.offers,
            },
        }),
        ...(opts.featureList && {featureList: opts.featureList}),
        ...(opts.keywords && {keywords: opts.keywords}),
        ...(opts.screenshot && {screenshot: opts.screenshot}),
        ...(creator !== null && {creator}),
    })
}

export function buildWebApplication(opts: WebApplicationOptions): JsonLd {
    return buildApplication('WebApplication', opts)
}

export function buildSoftwareApplication(opts: WebApplicationOptions): JsonLd {
    return buildApplication('SoftwareApplication', opts)
}

// ── ImageObject ──────────────────────────────────────────────────────────────────
//
// Per Google's image-SEO spec, ImageObject reinforces the per-image relevance signal
// already carried by alt text. No license/acquireLicensePage — the Licensable badge
// was dropped (2026-06-01: badge attracts stock-licensing intent narcis cannot serve).
// No creator/copyrightNotice — these are portraits of users' own faces; narcis is
// not the depicted person's copyright holder and the sitter context makes those fields
// legally awkward.
//
// representativeOfPage should be true on the single strongest portrait per page
// (cover_portrait / hero) so Google knows which image best represents the page.

export interface ImageObjectOptions {
    /** Absolute URL of the image (S3 src). */
    contentUrl: string
    /** Short descriptive name drawn from the same register vocabulary as the alt text. */
    name: string
    /** Caption reusing the register-specific alt. */
    caption: string
    /** True on the single strongest portrait per page (cover_portrait / hero). */
    representativeOfPage?: boolean
}

export function buildImageObject(opts: ImageObjectOptions): JsonLd {
    return jsonLd('ImageObject', {
        contentUrl: opts.contentUrl,
        name: opts.name,
        caption: opts.caption,
        ...(opts.representativeOfPage && {representativeOfPage: true}),
    })
}

// ── ImageGallery ─────────────────────────────────────────────────────────────────
//
// The wall homes' gallery: the pictures the document already server-renders, and nothing said
// about them — no name, caption or representativeOfPage. A text field here would put words on a
// picture the page itself never labels (Ovid's signed-out page carries no estate text at all).

export interface GalleryImage {
    /** Absolute URL of the image. */
    contentUrl: string
    width: number
    height: number
}

export interface ImageGalleryOptions {
    name: string
    url: string
    images: GalleryImage[]
}

export function buildImageGallery(opts: ImageGalleryOptions): JsonLd {
    return jsonLd('ImageGallery', {
        name: opts.name,
        url: opts.url,
        image: opts.images.map((i) => ({
            '@type': 'ImageObject',
            contentUrl: i.contentUrl,
            width: i.width,
            height: i.height,
        })),
    })
}
